import React from 'react';
import type {
    SearchSuggestion,
    SearchSuggestionGroup,
} from '../view-models/search-suggestion';
import { SearchSuggestionItem } from './SearchSuggestionItem';
import { highlightMatch } from '../utils/suggestion-highlight-match';
import { cn } from '../../../utils/cn';
import { useAccessibleClick } from '../../../hooks/use-accessible-click';
import { useSearchTracking } from '../hooks/use-search-tracking';

interface SearchSuggestionListProps {
    group: SearchSuggestionGroup;
    query: string;
    onOptionClick: (suggestion: SearchSuggestion | string) => void;
    variant?: 'default' | 'header';
    menuColor?: 'light' | 'dark';
    showProfessionals?: boolean;
    limit?: number;
}

const SUGGESTION_TYPE_TITLES = {
    keyword: 'Suggestions',
    categories: 'Categories',
    professionals: 'Companies',
} as const;

export const SearchSuggestionList: React.FC<SearchSuggestionListProps> = ({
    group,
    query,
    onOptionClick,
    variant = 'default',
    menuColor = 'dark',
    showProfessionals = true,
    limit = 5,
}) => {
    const trackingConfig = useSearchTracking();
    const isProfessionalGroup = group.type === 'professionals';

    return (
        <div>
            <span
                className={cn(
                    'font-medium md:mb-0 md:text-base',
                    variant === 'header' && menuColor === 'light'
                        ? 'text-white'
                        : 'text-gray-800',
                )}
            >
                {SUGGESTION_TYPE_TITLES[group.type] || ''}
            </span>
            <div
                className={cn(
                    'my-2 md:my-2.5',
                    isProfessionalGroup
                        ? 'space-y-1.5 md:space-y-3'
                        : 'space-y-2.5 md:space-y-1.5',
                )}
            >
                {group.options.slice(0, limit).map((option, optionIndex) => {
                    if (isProfessionalGroup && typeof option !== 'string') {
                        return showProfessionals ? (
                            <ProfessionalSuggestionItem
                                key={option.link || optionIndex}
                                suggestion={option}
                                query={query}
                                variant={variant}
                                menuColor={menuColor}
                                onClick={() => {
                                    onOptionClick(option);
                                    trackingConfig.onSuggestedCategoryClick(
                                        option.title,
                                    );
                                }}
                            />
                        ) : null;
                    }

                    return (
                        <SearchSuggestionItem
                            key={optionIndex}
                            label={
                                typeof option === 'string'
                                    ? option
                                    : option.title
                            }
                            type={
                                typeof option === 'string'
                                    ? undefined
                                    : option.type
                            }
                            query={query}
                            variant={variant}
                            menuColor={menuColor}
                            onClick={() => {
                                onOptionClick(option);
                                trackingConfig.onSuggestedCategoryClick(
                                    typeof option === 'string'
                                        ? option
                                        : option.title,
                                );
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );
};

/**
 * Component for displaying professional suggestions with company logo and name
 */
const ProfessionalSuggestionItem: React.FC<{
    suggestion: SearchSuggestion;
    query: string;
    variant: 'default' | 'header';
    menuColor: 'light' | 'dark';
    onClick: () => void;
}> = ({ suggestion, query, variant, menuColor, onClick }) => {
    const { onClick: handleClick, onKeyDown } = useAccessibleClick(onClick);

    return (
        <div
            onClick={handleClick}
            onKeyDown={onKeyDown}
            className={cn(
                'group flex cursor-pointer items-center gap-2 focus:outline-none',
                variant === 'header' && menuColor === 'light'
                    ? 'md:focus:bg-white/10'
                    : variant === 'header' && menuColor === 'dark'
                      ? 'md:focus:bg-gray-200'
                      : '',
            )}
            role="option"
            tabIndex={0}
            aria-selected={false}
        >
            {suggestion.image && (
                <div
                    className={cn(
                        'flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-100 p-1 md:border-0',
                        variant === 'default' &&
                            !suggestion.logoBackground &&
                            'bg-gray-100',
                    )}
                    style={{
                        backgroundColor: suggestion.logoBackground || undefined,
                    }}
                >
                    <img
                        src={suggestion.image}
                        alt={suggestion.title}
                        className={cn(
                            'h-full w-full object-contain',
                            variant === 'default' &&
                                !suggestion.logoBackground &&
                                'mix-blend-multiply',
                        )}
                    />
                </div>
            )}
            <span
                className={cn(
                    'inline-block max-w-full truncate border-b border-transparent text-dropdown leading-[1.1]',
                    variant === 'header' && menuColor === 'light'
                        ? 'text-white group-hover:border-white'
                        : variant === 'header' && menuColor === 'dark'
                          ? 'text-gray-800 group-hover:border-gray-800'
                          : 'text-gray-900 group-hover:border-gray-900',
                )}
            >
                {highlightMatch(suggestion.title, query, variant, menuColor)}
            </span>
        </div>
    );
};
